import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AdminSubNav, CHATBOT_TABS } from "@/components/admin/AdminSubNav";
import {
  listTrainingExamples,
  saveTrainingExample,
  setTrainingExampleEnabled,
  deleteTrainingExample,
  listChatFeedback,
  resolveChatFeedback,
  getPrecedingQuestion,
  type TrainingExampleRow,
  type FeedbackRow,
} from "@/lib/kb.functions";
import { Loader2, ThumbsDown, ThumbsUp, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/chatbot/training")({
  head: () => ({
    meta: [
      { title: "Training · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ChatbotTraining,
});

function ChatbotTraining() {
  const listFn = useServerFn(listTrainingExamples);
  const saveFn = useServerFn(saveTrainingExample);
  const toggleFn = useServerFn(setTrainingExampleEnabled);
  const deleteFn = useServerFn(deleteTrainingExample);
  const feedbackFn = useServerFn(listChatFeedback);
  const resolveFn = useServerFn(resolveChatFeedback);
  const questionFn = useServerFn(getPrecedingQuestion);

  const [examples, setExamples] = useState<TrainingExampleRow[] | null>(null);
  const [feedback, setFeedback] = useState<FeedbackRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  const refresh = useCallback(() => {
    listFn()
      .then(setExamples)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    feedbackFn()
      .then(setFeedback)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [listFn, feedbackFn]);

  useEffect(refresh, [refresh]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function promoteFeedback(row: FeedbackRow) {
    const asked = await questionFn({
      data: { conversationId: row.conversation_id, createdAt: row.created_at },
    }).catch(() => "");
    setQuestion(String(asked ?? ""));
    setAnswer(row.correction ?? "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div>
      <AdminSubNav tabs={CHATBOT_TABS} />
      <div className="space-y-6 p-4 sm:p-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold">Training</h1>
          <p className="text-sm text-muted-foreground">
            Teach SAM your preferred answers. Approved pairs are injected into every conversation,
            and thumbs-down corrections from visitors land in the review queue below.
          </p>
        </header>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <section className="rounded-xl border border-border p-4">
          <h2 className="text-sm font-semibold">Add a preferred answer</h2>
          <div className="mt-3 space-y-2">
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="When a visitor asks…"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={4}
              placeholder="SAM should say…"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={busy || !question.trim() || !answer.trim()}
            onClick={() =>
              void run(async () => {
                await saveFn({ data: { question, answer } });
                setQuestion("");
                setAnswer("");
              })
            }
            className="mt-3 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            Save example
          </button>
        </section>

        <section className="rounded-xl border border-border">
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">
            Approved answers
          </h2>
          {!examples ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : examples.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No examples yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {examples.map((ex) => (
                <li key={ex.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{ex.question}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                      {ex.answer}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={ex.enabled}
                      onChange={(e) =>
                        void run(() => toggleFn({ data: { id: ex.id, enabled: e.target.checked } }))
                      }
                    />
                    Active
                  </label>
                  <button
                    type="button"
                    aria-label="Delete example"
                    onClick={() => void run(() => deleteFn({ data: { id: ex.id } }))}
                    className="rounded p-1 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-border">
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">
            Visitor feedback
          </h2>
          {!feedback ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : feedback.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No ratings yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {feedback.map((row) => (
                <li key={row.id} className="space-y-2 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {row.rating === 1 ? (
                      <ThumbsUp className="h-4 w-4 text-brand" />
                    ) : (
                      <ThumbsDown className="h-4 w-4 text-destructive" />
                    )}
                    {new Date(row.created_at).toLocaleString()}
                    {row.resolved && <span className="text-brand">· handled</span>}
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{row.content}</p>
                  {row.correction && (
                    <p className="rounded-md bg-muted p-2 text-sm">
                      <span className="font-semibold">Suggested: </span>
                      {row.correction}
                    </p>
                  )}
                  <div className="flex gap-2">
                    {row.correction && (
                      <button
                        type="button"
                        onClick={() => void promoteFeedback(row)}
                        className="rounded-md border border-brand px-3 py-1.5 text-xs font-semibold text-brand"
                      >
                        Use as training example
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        void run(() => resolveFn({ data: { id: row.id, resolved: !row.resolved } }))
                      }
                      className="rounded-md border border-border px-3 py-1.5 text-xs"
                    >
                      {row.resolved ? "Reopen" : "Mark handled"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
